import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import StudentReceiptModal from './StudentReceiptModal';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Spin,
  message,
  Row,
  Col,
  Statistic,
  Descriptions,
  Divider,
  Alert,
  Layout
} from 'antd';
import {
  NumberOutlined,
  DollarOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './MyPerformance.css'
import getUserIdFromToken from '../../Utils/authUtils';

const { Title, Text } = Typography;
const { Content } = Layout;

function StudentBilling() {
  const { t } = useTranslation();
  const userId = getUserIdFromToken();
  const [billingData, setBillingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receiptModalVisible, setReceiptModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchStudentBilling = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        message.error(t('No token found. Please log in.'));
        return;
      }

      if (!userId) {
        message.error(t('Student ID not found. Please log in again.'));
        return;
      }

      const response = await axios.get(`${API_URL}/students/${userId}/payments`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setBillingData(response.data || []);
      
    } catch (error) {
      console.error('Error fetching billing data:', error);
      message.error(t('Error fetching billing history: ') + (error.response?.data?.message || t('Server error')));
    } finally {
      setLoading(false);
    }
  }, [API_URL, userId, t]);

  useEffect(() => {
    fetchStudentBilling();
  }, [fetchStudentBilling]);

  
const summaryStats = useMemo(() => {


  if (!billingData || billingData.length === 0) {
    return {
      totalPaid: 0,
      totalRemaining: 0,
      totalTransactions: 0,
      lastPayment: null
    };
  }

  if (billingData.length > 0) {

  }

  const totalPaid = billingData.reduce((sum, payment) => {
    const payAmount = parseFloat(payment.pay_amount || payment.final_price || 0);
    return sum + payAmount;
  }, 0);

  // ✅ Deduplicate latest remaining balance by id (not invoice_no)
  const latestById = {};
  billingData.forEach((payment) => {
    const existing = latestById[payment.id];
    const currentTime = existing ? new Date(existing.timestamp) : null;
    const newTime = new Date(payment.timestamp);

    if (!existing || newTime > currentTime) {
      latestById[payment.id] = payment;
    }
  });

  const totalRemaining = Object.values(latestById).reduce((sum: number, payment: any) => {
    const remaining = parseFloat(payment.remaining_balance || 0);
    return remaining > 0 ? sum + remaining : sum;
  }, 0);

  const totalTransactions = billingData.length;
  const lastPayment = billingData.length > 0 ? billingData[0] : null;

  return {
    totalPaid,
    totalRemaining,
    totalTransactions,
    lastPayment
  };
}, [billingData]);


  const handleViewReceipt = (payment) => {
    setSelectedPayment(payment);
    setReceiptModalVisible(true);
  };

  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return <DollarOutlined />;
      case 'credit card':
      case 'card':
        return <CreditCardOutlined />;
      default:
        return <DollarOutlined />;
    }
  };

  const getPaymentMethodColor = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return 'green';
      case 'credit card':
      case 'card':
        return 'blue';
      case 'bank transfer':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getConfirmationStatus = (status, isRefunded) => {
    if (isRefunded) {
      return <Tag color="red" icon={<ExclamationCircleOutlined />}>Refunded</Tag>;
    }
    
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return <Tag color="green" icon={<CheckCircleOutlined />}>Confirmed</Tag>;
      case 'pending':
        return <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>;
      default:
        return <Tag color="default">{status || 'Unknown'}</Tag>;
    }
  };

  const columns = [
    {
        title: t('date'),
        dataIndex: 'last_payment_date',
        key: 'last_payment_date',
        width: 120,
        render: (last_payment_date) => (
            <Space direction="vertical" size={0}>
            <Text strong>{dayjs(last_payment_date).format('MMM DD, YYYY')}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
                {dayjs(last_payment_date).format('HH:mm')}
            </Text>
            </Space>
        ),
        sorter: (a, b) => dayjs(a.last_payment_date).unix() - dayjs(b.last_payment_date).unix(),
        },
    {
      title: t('invoice'),
      dataIndex: 'invoice_no',
      key: 'invoice_no',
      width: 120,
      render: (invoiceNo) => (
        <Text code>{invoiceNo}</Text>
      ),
    },
    {
      title: t('class'),
      dataIndex: 'class_code',
      key: 'class_code',
      width: 100,
      render: (classCode) => (
        <Tag color="blue">{classCode || 'N/A'}</Tag>
      ),
    },
        {
      title: t('price'),
      dataIndex: 'final_price',
      key: 'final_price',
      width: 120,
      render: (finalPrice) => (
        <Text>฿{parseFloat(finalPrice || 0).toLocaleString()}</Text>
      ),
    },
    {
      title: t('amount_paid'),
      dataIndex: 'pay_amount',
      key: 'pay_amount',
      width: 120,
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          ฿{parseFloat(amount || 0).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => parseFloat(a.pay_amount || 0) - parseFloat(b.pay_amount || 0),
    },

    {
      title: t('remaining'),
      dataIndex: 'remaining_balance',
      key: 'remaining_balance',
      width: 140,
      render: (balance) => {
        const amount = parseFloat(balance || 0);
        return (
          <Text style={{ color: amount > 0 ? '#ff4d4f' : '#52c41a' }}>
            ฿{amount.toLocaleString()}
          </Text>
        );
      },
      sorter: (a, b) => parseFloat(a.remaining_balance || 0) - parseFloat(b.remaining_balance || 0),
    },
    {
      title: t('paymentMethod'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 130,
      render: (method) => (
        <Tag color={getPaymentMethodColor(method)} icon={getPaymentMethodIcon(method)}>
          {method || 'N/A'}
        </Tag>
      ),
      filters: [
        { text: 'Cash', value: 'cash' },
        { text: 'Credit Card', value: 'credit card' },
        { text: 'Bank Transfer', value: 'bank transfer' },
      ],
      onFilter: (value, record) => record.payment_method?.toLowerCase().includes(value),
    },
    {
      title: t('receipt'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<NumberOutlined />}
          onClick={() => handleViewReceipt(record)}
        >
          {t('receipt')}
        </Button>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const inventoryItems = record.inventory_items ? JSON.parse(record.inventory_items) : [];
    
    return (
      <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Descriptions title="Student" size="small" column={1}>
              <Descriptions.Item label="Student">
                {record.firstname} {record.lastname}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {record.phone || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {record.address || 'N/A'}
              </Descriptions.Item>
              {record.memo && (
                <Descriptions.Item label="Memo">
                  {record.memo}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions title="Course" size="small" column={1}>
              <Descriptions.Item label="Course Price">
                ฿{parseFloat(record.price || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Materials Price">
                ฿{parseFloat(record.inventory_price || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Discount">
                ฿{parseFloat(record.discount || 0).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="VAT">
                ฿{parseFloat(record.vat || 0).toLocaleString()}
              </Descriptions.Item>
              {record.promotion && (
                <Descriptions.Item label="Promotion">
                  <Tag color="gold">{record.promotion}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
        </Row>
        
        {inventoryItems && inventoryItems.length > 0 && (
          <>
            <Divider />
            <Title level={5}>
              <ShoppingCartOutlined /> Materials Purchased
            </Title>
            <Table
              size="small"
              pagination={false}
              dataSource={inventoryItems}
              columns={[
                {
                  title: 'Item',
                  dataIndex: 'label',
                  key: 'label',
                },
                {
                  title: 'Price per Unit',
                  dataIndex: 'price_per_unit',
                  key: 'price_per_unit',
                  render: (price) => `฿${parseFloat(price || 0).toLocaleString()}`,
                },
                {
                  title: 'Total Price',
                  dataIndex: 'inventory_price',
                  key: 'inventory_price',
                  render: (price) => `฿${parseFloat(price || 0).toLocaleString()}`,
                },
              ]}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div>
        <Sidebar />
        <Header />

    <Content  className="performance-content">
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Total Paid')}
              value={summaryStats.totalPaid}
              prefix="฿"
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => value.toLocaleString()}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
           <Statistic
                title={t('Outstanding Balance')}
                value={summaryStats.totalRemaining as number}
                prefix="฿"
                valueStyle={{ color: (summaryStats.totalRemaining as number) > 0 ? '#ff4d4f' : '#52c41a' }}
                formatter={(value) => (value as number).toLocaleString()}
                />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Total Transactions')}
              value={summaryStats.totalTransactions}
              prefix={<NumberOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Last Payment')}
              value={summaryStats.lastPayment ? dayjs(summaryStats.lastPayment.timestamp).format('MMM DD, YYYY') : 'N/A'}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Outstanding Balance Alert */}
      {Number(summaryStats.totalRemaining) > 0 && (
        <Alert
          message={t('Outstanding Balance')}
          description={`You have an outstanding balance of ฿${summaryStats.totalRemaining.toLocaleString()}. Please contact the office for payment arrangements.`}
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Billing History Table */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={billingData}
            rowKey="id"
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => true,
            }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} transactions`,
            }}
            scroll={{ x: 'max-content' }}
            style={{ width: '100%', overflowX: 'auto' }}
            locale={{
              emptyText: loading ? 'Loading...' : 'No billing history found'
            }}
          />
        </Spin>
      </Card>

      {/* Receipt Modal */}
      <StudentReceiptModal
        visible={receiptModalVisible}
        onClose={() => setReceiptModalVisible(false)}
        paymentData={selectedPayment}
      />

        </Content>
    </div>
  );
}

export default withRoleAccess(StudentBilling, ['student']);